import {
  ContentBlock,
  Heading,
  Text,
  TextField,
  Checkbox,
  SelectField,
} from '@cinch-labs/design-system'
import { useEffect, useRef, useState } from 'react'
import styles from './contact-details.module.css'
import { useMaintenanceContext } from '../../context/maintenance-context'
import { CarMaintenanceStepWrapper } from '../car-maintenance-step-wrapper/car-maintenance-step-wrapper'
import { useForm } from 'react-hook-form'
import {
  EventNames,
  TrackingEventTypes,
  useAnalytics,
} from '@cinch-nx/shared-analytics'
import { isValidPostcode } from '../../utils/postcode-helper'
import { ContactTimePreference } from '../../types'
import { datadogRum } from '@datadog/browser-rum'
import Joi from 'joi'
import { BillingAddress } from '../billing-address/billing-address'
import { getAddressDetails } from '../../services/address-io-service'

export type FormData = {
  firstName: string
  lastName: string
  mobileNumber: string
  emailAddress: string
  postcode: string
  contactTimePreference: ContactTimePreference
  emailPermissions: boolean
  addressLine1?: string
  addressLine2?: string
  city?: string
  county?: string
  selectedAddress?: {
    suggestedAddress: string
    addressId: string
  }
  enteredPostcode?: string
  title?: string
}

const isValidMobileNumber = (mobileNumber: string) => {
  const phoneNumberRegex = /(0)(\s*[0-9]){10}/
  return phoneNumberRegex.test(mobileNumber)
}

const isValidEmail = (email: string) => {
  const schema = Joi.string().email({ tlds: { allow: true } })
  const result = schema.validate(email)
  return !result.error
}

export const ContactDetails = () => {
  const { trackEvent } = useAnalytics()

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    clearErrors,
    watch,
    reset,
  } = useForm<FormData>()

  const {
    dispatch,
    step,
    customerFirstName,
    customerSurname,
    postcode,
    customerPhone,
    customerEmail,
    emailPermissions,
    canBookAppointment,
    customerTitle,
  } = useMaintenanceContext()

  const [hasSelectAddressError, setHasSelectAddressError] =
    useState<boolean>(false)
  const [overrideAddressError, setOverrideAddressError] =
    useState<boolean>(false)

  const emailChecked = watch('emailPermissions')
  const trackedEvent = useRef(false)

  useEffect(() => {
    setValue('title', customerTitle || '')
    setValue('firstName', customerFirstName || '')
    setValue('lastName', customerSurname || '')
    setValue('mobileNumber', customerPhone || '')
    setValue('postcode', postcode || '')
    setValue('emailAddress', customerEmail || '')
    if (emailPermissions === undefined) {
      setValue('emailPermissions', false)
    } else {
      setValue('emailPermissions', !emailPermissions)
    }
  }, [
    customerTitle,
    customerPhone,
    postcode,
    customerSurname,
    customerFirstName,
    customerEmail,
    emailPermissions,
  ])

  useEffect(() => {
    if (!trackedEvent.current) {
      const contactDetailsEvent = {
        eventName: `Appointment update page displayed`,
        type: TrackingEventTypes.ADOBE,
        data: {
          event: {
            name: EventNames.load,
            category: 'cinch_servicing',
            action: 'contact_details_page_load',
            label: canBookAppointment ? 'booking_journey' : 'enquiry_journey',
          },
        },
      }
      trackEvent(contactDetailsEvent)
    }
    trackedEvent.current = true
  })

  const submitForm = async (data: FormData) => {
    datadogRum.addAction('Cinch Servicing: Contact Info Added', {
      cinchServicing: true,
      step,
      page: window.location.pathname,
      customerEmail,
      emailPermissions,
      canBookAppointment,
    })

    const contactDetailsContinueEvent = {
      eventName: 'contact-details-continue-click',
      type: TrackingEventTypes.ADOBE,
      data: {
        event: {
          name: EventNames.click,
          category: 'cinch_servicing',
          action: 'contact_details_continue',
          label: canBookAppointment ? 'booking_journey' : 'enquiry_journey',
        },
      },
    }
    trackEvent(contactDetailsContinueEvent)

    let emailPermissionsToDispatch
    if (emailPermissions === undefined) {
      emailPermissionsToDispatch = !data.emailPermissions
    } else if (emailPermissions === data.emailPermissions) {
      emailPermissionsToDispatch = !emailPermissions
    } else {
      emailPermissionsToDispatch = emailPermissions
    }

    const address = data.selectedAddress?.addressId
      ? await getAddressDetails(data.selectedAddress.addressId)
      : {
          addressLine1: data.addressLine1,
          county: data.county,
          postTown: data.city,
          postcode: data.postcode,
          addressLine2: data.addressLine2,
        }

    if (canBookAppointment && !address?.addressLine1) {
      setOverrideAddressError(true)
      return
    }

    dispatch({
      type: 'SET_CUSTOMER_DETAILS',
      title: data.title,
      phone: data.mobileNumber,
      firstName: data.firstName,
      lastName: data.lastName,
      email: data.emailAddress,
      postcode: data.postcode,
      contactTimePreference: data.contactTimePreference,
      emailPermissions: emailPermissionsToDispatch,
      ...(canBookAppointment
        ? {
            customerAddress: {
              addressLine1: address?.addressLine1 || '',
              county: address?.county || '',
              postcode: address?.postcode || '',
              postTown: address?.postTown || '',
              addressLine2: address?.addressLine2,
            },
          }
        : {}),
    })
  }

  return (
    <CarMaintenanceStepWrapper
      title={'Contact details'}
      onContinue={handleSubmit(submitForm)}
    >
      <form onSubmit={handleSubmit(submitForm)}>
        <div>
          {canBookAppointment && (
            <SelectField
              label="Title"
              id="contact_time_input"
              options={[
                { label: 'Mr', value: 'Mr' },
                { label: 'Mrs', value: 'Mrs' },
                { label: 'Miss', value: 'Miss' },
                { label: 'Ms', value: 'Ms' },
                { label: 'Mx', value: 'Mx' },
              ]}
              classNames={{
                container: styles.selectField,
                option: styles.selectFieldText,
                select: styles.selectFieldText,
              }}
              placeholder={'Please select'}
              {...register('title', {
                required: 'Please select a title',
              })}
              error={{
                show: !!errors.title,
                message: errors.title?.message as string,
              }}
            />
          )}
          <TextField
            id="first_name_input"
            label="First name"
            type="text"
            aria-describedby={'first name input'}
            fullWidth
            classNames={{
              container: styles.formTextField,
            }}
            {...register('firstName', {
              required: 'Please enter your first name.',
            })}
            onChange={(e) => {
              clearErrors()
              setValue('firstName', e.target.value)
            }}
            error={{
              show: !!errors.firstName,
              message: errors.firstName?.message as string,
            }}
          />
          <TextField
            id="last_name_input"
            label="Last name"
            type="text"
            aria-describedby={'last name input'}
            fullWidth
            classNames={{
              container: styles.formTextField,
            }}
            {...register('lastName', {
              required: 'Please enter your last name.',
            })}
            onChange={(e) => {
              clearErrors()
              setValue('lastName', e.target.value)
            }}
            error={{
              show: !!errors.lastName,
              message: errors.lastName?.message as string,
            }}
          />
          <TextField
            id="email_input"
            label="Email address"
            type="text"
            aria-describedby={'email input'}
            fullWidth
            classNames={{
              container: styles.formTextField,
            }}
            {...register('emailAddress', {
              validate: (value) =>
                isValidEmail(value) || 'Please enter a valid email address.',
            })}
            onChange={(e) => {
              clearErrors()
              setValue('emailAddress', e.target.value)
            }}
            error={{
              show: !!errors.emailAddress,
              message: errors.emailAddress?.message as string,
            }}
          />
          <TextField
            id="mobile_number_input"
            label="Mobile number"
            type="tel"
            aria-describedby={'mobile input'}
            fullWidth
            classNames={{
              container: styles.formTextField,
            }}
            {...register('mobileNumber', {
              validate: (value) =>
                isValidMobileNumber(value) ||
                'Please enter a valid 11 digit mobile number.',
            })}
            onChange={(e) => {
              clearErrors()
              setValue('mobileNumber', e.target.value)
            }}
            error={{
              show: !!errors.mobileNumber,
              message: errors.mobileNumber?.message as string,
            }}
          />
          {!canBookAppointment && (
            <TextField
              id="postcode_input"
              label="Postcode"
              type="text"
              aria-describedby={'postcode input'}
              {...register('postcode', {
                onChange: (e) => {
                  clearErrors()
                  setValue('postcode', e.target.value)
                },
                validate: (value) =>
                  isValidPostcode(value) ||
                  'Please enter a valid postcode (e.g. PR15JW).',
              })}
              error={{
                show: !!errors.postcode,
                message: errors.postcode?.message as string,
              }}
            />
          )}
          {!canBookAppointment && (
            <SelectField
              label="Best time to reach you"
              id="contact_time_input"
              options={[
                { label: 'AM', value: 'AM' },
                { label: 'PM', value: 'PM' },
              ]}
              {...register('contactTimePreference')}
              classNames={{ container: styles.selectField }}
            />
          )}
          {canBookAppointment && (
            <div>
              <BillingAddress
                register={register}
                errors={errors}
                hasSelectAddressError={hasSelectAddressError}
                setHasSelectAddressError={setHasSelectAddressError}
                overrideAddressError={overrideAddressError}
                setOverrideAddressError={setOverrideAddressError}
                setValue={setValue}
                clearErrors={clearErrors}
                reset={reset}
              />
            </div>
          )}
        </div>
        <div className={styles.divider} />

        <div>
          <ContentBlock className={styles.marketingPermissionsHeading}>
            <Heading element="h4">Marketing preferences</Heading>
            <Text fontSizeFixed="m">
              We’ll let you know about the latest offers, news, products and
              services from cinch that we feel may be of interest to you. If
              you’ve already told us not to contact you via email, we won’t
              update your preferences.
            </Text>
            <Text fontSizeFixed="m">
              Please tick if you do not wish to hear from us via email.
            </Text>
          </ContentBlock>
          <div className={styles.checkBoxContainer}>
            <Checkbox
              label={'Email'}
              name="checkboxEmail"
              checked={!!emailChecked}
              onChange={(e) => {
                setValue('emailPermissions', e.target.checked)
              }}
            />
          </div>
        </div>
      </form>
    </CarMaintenanceStepWrapper>
  )
}

export default ContactDetails
